import dayjs from "dayjs";
import { belvedereContract, BELVEDERE_FIRST_CHILD_FREE_ROOM_CODES } from "../data/belvedereContractData";
import { buildPdfPerPersonGuestChargePercents, resolvePdfAdultSlotChargePercent, resolvePdfInfantChargePercent, resolvePdfChildSlotChargePercent } from "../data/belvedereContractReductions";
import { buildFlightOfferPricingDisplay } from "../Components/FlightBookingFlowMockup/adapters/flightOfferAdapter";

export const BELVEDERE_INFANT_MAX_AGE = 1.99;
export const BELVEDERE_CHILD_MAX_AGE = 11.99;
const INFANT_MAX_AGE = BELVEDERE_INFANT_MAX_AGE;
const CHILD_MAX_AGE = BELVEDERE_CHILD_MAX_AGE;
/** Default child age when count is known but ages are missing (not an infant). */
const DEFAULT_CHILD_AGE_YEARS = 6;

const BELVEDERE_FIRST_CHILD_FREE_ROOM_CODE_SET = new Set(
  BELVEDERE_FIRST_CHILD_FREE_ROOM_CODES.map((code) => Number(code)),
);

/**
 * Normalize campaign search room occupancy.
 * Fixes malformed shapes such as `children: [7]` (ages array mistaken for count),
 * coerces ages to finite numbers, and aligns children count with childAges length.
 */
export function normalizeBelvedereCampaignRoomOccupancy(room) {
  let adults = Math.max(0, Math.floor(Number(room?.adults) || 0));
  if (!Number.isFinite(adults) || adults < 1) adults = 2;
  adults = Math.min(8, adults);

  let childAges = [];
  let childrenRaw = room?.children;

  // Corrupt / legacy: children sent as ages array, e.g. { children: [7] }
  if (Array.isArray(childrenRaw)) {
    childAges = childrenRaw
      .map((age) => Number(age))
      .filter((age) => Number.isFinite(age) && age >= 0 && age <= 17);
    childrenRaw = childAges.length;
  }

  let children = Math.max(0, Math.min(6, Math.floor(Number(childrenRaw) || 0)));

  if (Array.isArray(room?.childAges)) {
    const fromField = room.childAges
      .map((age) => Number(age))
      .filter((age) => Number.isFinite(age) && age >= 0 && age <= 17);
    if (fromField.length > 0) {
      childAges = fromField;
    }
  }

  if (childAges.length > children) {
    children = Math.min(6, childAges.length);
  }
  while (childAges.length < children) {
    childAges.push(DEFAULT_CHILD_AGE_YEARS);
  }
  childAges = childAges.slice(0, children);

  return { adults, children, childAges };
}

export function normalizeBelvedereCampaignRooms(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  return list.slice(0, 6).map((room) => normalizeBelvedereCampaignRoomOccupancy(room));
}

/** Room qualifies for PDF first-child-free (100% reduction) benefit. */
export function isBelvedereFirstChildFreeRoom(room) {
  const code = Number(room?.code);
  return BELVEDERE_FIRST_CHILD_FREE_ROOM_CODE_SET.has(code);
}

/** Whether any room in the offer has first-child-free per contract. */
export function offerHasBelvedereFirstChildFreeBenefit(quote) {
  if (!quote) return false;
  const breakdown = Array.isArray(quote.roomBreakdown) ? quote.roomBreakdown : [];
  if (breakdown.length > 0) {
    return breakdown.some((row) => isBelvedereFirstChildFreeRoom(row.room));
  }
  return isBelvedereFirstChildFreeRoom(quote.room);
}

/**
 * Show highlight when search has 2+ adults and at least one paying child (2–11.99).
 * If child ages are not filled yet, any child with 2 adults is enough.
 */
export function searchEligibleForBelvedereFirstChildFreeHighlight(searchRooms) {
  const list = Array.isArray(searchRooms) ? searchRooms : [];
  if (list.length === 0) return false;
  return list.some((occ) => {
    const { adults, children, childAges } = normalizeBelvedereCampaignRoomOccupancy(occ);
    if (adults < 2 || children < 1) return false;
    const hadExplicitAges =
      (Array.isArray(occ?.childAges) && occ.childAges.length > 0) || Array.isArray(occ?.children);
    if (!hadExplicitAges) return true;
    return childAges.some((age) => age > INFANT_MAX_AGE && age <= CHILD_MAX_AGE);
  });
}

export function shouldHighlightBelvedereFirstChildFree(quote, searchRooms) {
  return (
    offerHasBelvedereFirstChildFreeBenefit(quote) &&
    searchEligibleForBelvedereFirstChildFreeHighlight(searchRooms)
  );
}

/** Hotel-only search: room unit does not charge the first child. */
export const BELVEDERE_FIRST_CHILD_FREE_BADGE_HE = "ילד ראשון ללא עלות";
export const BELVEDERE_FIRST_CHILD_FREE_HINT_HE =
  "ההטבה חלה על לינה במלון — הילד לא מוסיף לעלות החדר";

/**
 * Hotel+flight package: hotel benefit only — child flight is still charged.
 * Prefer these over the hotel-only strings when flights are bundled.
 */
export const BELVEDERE_FIRST_CHILD_FREE_PACKAGE_BADGE_HE = "ילד ראשון ללא עלות במלון";
export const BELVEDERE_FIRST_CHILD_FREE_PACKAGE_HINT_HE =
  "ההטבה חלה על לינה במלון בלבד. מחיר הטיסה לילד מחויב בנפרד";
export const BELVEDERE_FIRST_CHILD_FREE_PACKAGE_NOTE_HE =
  "ילד ראשון ללא עלות במלון · הטיסה לילד בתשלום";

function parseDate(value) {
  return dayjs(value, "YYYY-MM-DD", true);
}

function contractSeasons(contract) {
  return Array.isArray(contract?.seasons) ? contract.seasons : [];
}

function isContractLike(value) {
  return Boolean(
    value &&
      typeof value === "object" &&
      (value.hotelCode || Array.isArray(value.seasons) || Array.isArray(value.roomTypes)),
  );
}

function resolveContractAndPricingOptions(
  contractOrOptions,
  maybePricingOptions,
  fallbackContract = belvedereContract,
) {
  if (isContractLike(contractOrOptions)) {
    return {
      contract: contractOrOptions,
      pricingOptions: maybePricingOptions && typeof maybePricingOptions === "object" ? maybePricingOptions : {},
    };
  }
  return {
    contract: fallbackContract,
    pricingOptions:
      contractOrOptions && typeof contractOrOptions === "object" ? contractOrOptions : {},
  };
}

export function getSeasonForDate(date, contract = belvedereContract) {
  if (typeof contract?.resolveSeasonForDate === "function") {
    return contract.resolveSeasonForDate(date);
  }
  const d = parseDate(date);
  if (!d.isValid()) return null;
  for (const season of contractSeasons(contract)) {
    const from = parseDate(season.from);
    const to = parseDate(season.to);
    if ((d.isSame(from) || d.isAfter(from)) && (d.isSame(to) || d.isBefore(to))) {
      return season.code;
    }
  }
  return null;
}

export function getStayNights(arrivalDate, departureDate) {
  const arrival = parseDate(arrivalDate);
  const departure = parseDate(departureDate);
  if (!arrival.isValid() || !departure.isValid()) return 0;
  return Math.max(0, departure.diff(arrival, "day"));
}

export function getNightlySeasonBreakdown(arrivalDate, departureDate, contract = belvedereContract) {
  const nights = [];
  const arrival = parseDate(arrivalDate);
  const departure = parseDate(departureDate);
  if (!arrival.isValid() || !departure.isValid() || !departure.isAfter(arrival)) {
    return nights;
  }
  let cursor = arrival;
  while (cursor.isBefore(departure)) {
    const iso = cursor.format("YYYY-MM-DD");
    nights.push({ date: iso, season: getSeasonForDate(iso, contract) });
    cursor = cursor.add(1, "day");
  }
  return nights;
}

function countInfants(childAges) {
  return childAges.filter((age) => age >= 0 && age <= INFANT_MAX_AGE).length;
}

function countPayingChildren(childAges) {
  return childAges.filter((age) => age > INFANT_MAX_AGE && age <= CHILD_MAX_AGE).length;
}

function countOlderChildren(childAges) {
  return childAges.filter((age) => age > CHILD_MAX_AGE).length;
}

export function validateStayDates(arrivalDate, departureDate, contract = belvedereContract) {
  if (!arrivalDate || !departureDate) return "יש לבחור תאריכי הגעה ויציאה";
  const arrival = parseDate(arrivalDate);
  const departure = parseDate(departureDate);
  if (!arrival.isValid() || !departure.isValid()) return "תאריך לא תקין";
  if (!departure.isAfter(arrival)) return "תאריך יציאה חייב להיות אחרי תאריך הגעה";

  const nights = getStayNights(arrivalDate, departureDate);
  if (nights < contract.minStayNights) {
    return `מינימום שהייה: ${contract.minStayNights} לילות`;
  }

  const nightly = getNightlySeasonBreakdown(arrivalDate, departureDate, contract);
  if (nightly.some((n) => !n.season)) {
    return (
      contract.outOfSeasonMessage ||
      "חלק מתאריכי השהייה מחוץ לעונת החוזה (אפריל–נובמבר 2026)"
    );
  }

  const maxMinStay = Math.max(
    ...nightly.map(
      (n) => contractSeasons(contract).find((s) => s.code === n.season)?.minStayNights ?? contract.minStayNights,
    ),
  );
  if (nights < maxMinStay) {
    return `מינימום שהייה לעונה שנבחרה: ${maxMinStay} לילות`;
  }

  return null;
}

function maxAllowedChildren(room, adults) {
  const remainingCapacity = room.maxOccupancy - adults;
  return Math.max(0, Math.min(room.maxChildren, remainingCapacity));
}

function payingGuestsForRoom(occupancy) {
  const olderAsAdults = countOlderChildren(occupancy.childAges);
  const payingChildren = countPayingChildren(occupancy.childAges);
  return occupancy.adults + olderAsAdults + payingChildren;
}

function calculatePerPersonRoomNet(room, occupancy, arrivalDate, departureDate, contract = belvedereContract) {
  const nightly = getNightlySeasonBreakdown(arrivalDate, departureDate, contract);
  let total = 0;

  for (const night of nightly) {
    if (!night.season) continue;
    const baseRate = room.seasonalPrices[night.season];
    const guestPercents = buildPdfPerPersonGuestChargePercents(room, occupancy, night.season);
    for (const pct of guestPercents) {
      total += baseRate * (pct / 100);
    }
  }

  return Math.round(total * 100) / 100;
}

export function validateRoomOccupancy(occupancy, room) {
  const { adults, children, childAges } = normalizeBelvedereCampaignRoomOccupancy(occupancy);
  const totalGuests = adults + children;

  if (children > 0 && childAges.length !== children) return "יש להזין גיל לכל ילד";
  if (childAges.some((age) => age < 0 || age > 17)) return "גיל ילד חייב להיות בין 0 ל-17";

  if (adults < room.minAdults) return `נדרשים לפחות ${room.minAdults} מבוגרים`;
  if (adults > room.maxAdults) return `מקסימום ${room.maxAdults} מבוגרים`;

  const allowedChildren = maxAllowedChildren(room, adults);
  if (children > allowedChildren) {
    return allowedChildren === 0
      ? "ילדים אינם מורשים בתפוסה זו"
      : `מקסימום ${allowedChildren} ילדים בתפוסה זו`;
  }

  if (totalGuests < room.minOccupancy || totalGuests > room.maxOccupancy) {
    return `תפוסה מותרת: ${room.occupancyLabel}`;
  }

  const infants = countInfants(childAges);
  if (!room.infantsAllowed && infants > 0) return "תינוקות אינם מורשים בחדר זה";
  if ([4, 5, 6].includes(room.code) && infants > 0 && totalGuests > room.maxOccupancy) {
    return "תינוקות אינם מורשים מעבר לתפוסה מקסימלית";
  }

  const olderChildren = countOlderChildren(childAges);
  if (olderChildren > 0 && room.chargeType === "per_unit" && room.maxChildren === 0) {
    return "ילדים מעל גיל 12 אינם מותרים בחדר זה";
  }

  if (room.chargeType === "per_person") {
    const payingGuests = payingGuestsForRoom(occupancy);
    if (payingGuests > room.maxOccupancy) {
      return `תפוסה מותרת: עד ${room.maxOccupancy} אורחים בתשלום`;
    }
    if (payingGuests < room.minOccupancy) {
      return `תפוסה מותרת: ${room.occupancyLabel}`;
    }
  }

  return null;
}

/**
 * Alternate compositions for the same physical party: promote non-infant children
 * into adult slots (oldest first). Infants are never promoted.
 * Ordered by fewest promotions first (exact search occupancy is index 0).
 */
export function buildFlexibleOccupancyCandidates(occupancy) {
  const normalized = normalizeBelvedereCampaignRoomOccupancy(occupancy);
  const adults = normalized.adults;
  const children = normalized.children;
  const hadExplicitAges =
    (Array.isArray(occupancy?.childAges) && occupancy.childAges.length > 0) ||
    Array.isArray(occupancy?.children);
  const childAges = hadExplicitAges
    ? normalized.childAges
    : Array.from({ length: children }, () => DEFAULT_CHILD_AGE_YEARS);

  const infantAges = childAges.filter((age) => age >= 0 && age <= INFANT_MAX_AGE);
  const promotableAges = childAges
    .filter((age) => age > INFANT_MAX_AGE)
    .sort((a, b) => b - a);

  const candidates = [];
  for (let promote = 0; promote <= promotableAges.length; promote += 1) {
    const keptPromotable = promotableAges.slice(promote);
    const keptAges = [...keptPromotable, ...infantAges];
    candidates.push({
      adults: adults + promote,
      children: keptAges.length,
      childAges: keptAges,
    });
  }
  return candidates;
}

/**
 * Map search occupancy to a contract-legal occupancy for this room.
 * Prefer exact match; else fewest child→adult promotions that satisfy room rules
 * (e.g. 1A+2C → 2A+1C or 3A on superior rooms).
 * Returns null when the party cannot fit the room even after remapping.
 */
export function resolveCompatibleOccupancyForRoom(occupancy, room) {
  const candidates = buildFlexibleOccupancyCandidates(occupancy);
  for (let i = 0; i < candidates.length; i += 1) {
    const pricingOccupancy = candidates[i];
    if (validateRoomOccupancy(pricingOccupancy, room) === null) {
      return {
        pricingOccupancy,
        searchOccupancy: occupancy,
        isFlexibleMatch: i > 0,
        promotedChildren: i,
      };
    }
  }
  return null;
}

export function isRoomCompatible(occupancy, room) {
  return validateRoomOccupancy(occupancy, room) === null;
}

/** Search matching: exact occupancy or flexible child→adult remapping. */
export function isRoomCompatibleForSearch(occupancy, room) {
  return resolveCompatibleOccupancyForRoom(occupancy, room) != null;
}

export function usesAdultChildPricing(room) {
  return room?.pricingKind === "base" || room?.pricingKind === "supplement";
}

export function classifyAdultChildGuests(occupancy, room = {}) {
  const { adults, childAges } = normalizeBelvedereCampaignRoomOccupancy(occupancy);
  const from = Number(room.childAgeFrom);
  const to = Number(room.childAgeTo);
  const ageFrom = Number.isFinite(from) ? from : 2;
  const ageTo = Number.isFinite(to) ? to : 12;
  let adultCount = adults;
  let childCount = 0;
  for (const age of childAges) {
    if (age < ageFrom) continue;
    if (age <= ageTo) childCount += 1;
    else adultCount += 1;
  }
  return { adultCount, childCount };
}

function resolveBaseRoomForSupplement(room, contract) {
  const rooms = contract?.roomTypes || [];
  return (
    rooms.find(
      (row) =>
        (room.baseRoomCode != null && String(row.code) === String(room.baseRoomCode)) ||
        (room.baseRoomId && row.id === room.baseRoomId),
    ) || null
  );
}

function resolveSupplementChildPerNight(room) {
  if (room.supplementChildPerNight != null && Number.isFinite(Number(room.supplementChildPerNight))) {
    return Number(room.supplementChildPerNight);
  }
  const adultSup = Number(room.supplementAdultPerNight) || 0;
  const pct = Number(room.supplementChildPercentOfAdult);
  return adultSup * ((Number.isFinite(pct) ? pct : 50) / 100);
}

function adultNightlyRate(room, night) {
  if (night?.season && room.seasonalPrices?.[night.season] != null) {
    const seasonal = Number(room.seasonalPrices[night.season]);
    if (Number.isFinite(seasonal) && seasonal > 0) return seasonal;
  }
  const adult = Number(room.adultNightlyNet);
  return Number.isFinite(adult) ? adult : 0;
}

function singleNightlyRate(room, night, doubleRate) {
  if (night?.season && room.seasonalSinglePrices?.[night.season] != null) {
    const seasonal = Number(room.seasonalSinglePrices[night.season]);
    if (Number.isFinite(seasonal) && seasonal > 0) return seasonal;
  }
  const single = Number(room.singleNightlyNet);
  if (Number.isFinite(single) && single > 0) return single;
  return doubleRate;
}

export function dateSupplementsForNight(contract, iso) {
  const list = Array.isArray(contract?.dateSupplements) ? contract.dateSupplements : [];
  const key = String(iso || "").slice(0, 10);
  return list.filter((row) => {
    const from = String(row?.from || "").slice(0, 10);
    const to = String(row?.to || "").slice(0, 10);
    return Boolean(from && to && key >= from && key <= to);
  });
}

function dateSupplementAmounts(contract, iso, adultCount, childCount) {
  let adult = 0;
  let child = 0;
  for (const row of dateSupplementsForNight(contract, iso)) {
    adult += adultCount * (Number(row.adult) || 0);
    child += childCount * (Number(row.child) || 0);
  }
  return { adult, child };
}

function extraAdultPercent(room, contract) {
  const raw = room?.extraAdultRatePercent ?? contract?.extraAdultRatePercent;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n / 100 : 1;
}

function childPercentForOccupancy(room, adultCount) {
  const sharing = (Number(room.childRatePercent) >= 0 ? Number(room.childRatePercent) : 50) / 100;
  const ownRaw = Number(room.childOwnRoomRatePercent);
  const own = Number.isFinite(ownRaw) && ownRaw >= 0 ? ownRaw / 100 : sharing;
  return adultCount === 0 ? own : sharing;
}

function adultNightlyCharge(room, night, adultCount, contract) {
  const doubleRate = adultNightlyRate(room, night);
  if (adultCount <= 0) return 0;
  if (adultCount === 1) return singleNightlyRate(room, night, doubleRate);
  const extraPct = extraAdultPercent(room, contract);
  return 2 * doubleRate + (adultCount - 2) * doubleRate * extraPct;
}

function calculateAdultChildRoomNet(room, occupancy, arrivalDate, departureDate, contract) {
  const nightly = getNightlySeasonBreakdown(arrivalDate, departureDate, contract);
  const { adultCount, childCount } = classifyAdultChildGuests(occupancy, room);
  const childPct = childPercentForOccupancy(room, adultCount);
  let total = 0;
  for (const night of nightly) {
    if (!night.season) continue;
    const doubleRate = adultNightlyRate(room, night);
    const adultsNet = adultNightlyCharge(room, night, adultCount, contract);
    const childrenNet = childCount * doubleRate * childPct;
    const extras = dateSupplementAmounts(contract, night.date, adultCount, childCount);
    total += adultsNet + childrenNet + extras.adult + extras.child;
  }
  return Math.round(total * 100) / 100;
}

function calculateSupplementRoomNet(room, occupancy, arrivalDate, departureDate, contract) {
  const base = resolveBaseRoomForSupplement(room, contract);
  const baseNet = base
    ? calculateRoomNet(base, occupancy, arrivalDate, departureDate, contract)
    : 0;
  const nightly = getNightlySeasonBreakdown(arrivalDate, departureDate, contract);
  const nights = nightly.filter((night) => night.season).length;
  const { adultCount, childCount } = classifyAdultChildGuests(occupancy, base || room);
  const extras =
    nights *
    (adultCount * (Number(room.supplementAdultPerNight) || 0) +
      childCount * resolveSupplementChildPerNight(room));
  return Math.round((baseNet + extras) * 100) / 100;
}

export function validateRoomStayWindow(room, arrivalDate, departureDate) {
  if (!room?.stayFrom && !room?.stayTo) return null;
  if (!arrivalDate || !departureDate) return null;
  const from = room.stayFrom || "0000-01-01";
  const to = room.stayTo || "9999-12-31";
  const arrive = String(arrivalDate).slice(0, 10);
  const lastNight = parseDate(departureDate).subtract(1, "day").format("YYYY-MM-DD");
  if (arrive < from || lastNight > to) {
    return "חלק מתאריכי השהייה מחוץ לחלון החדר";
  }
  return null;
}

function calculateRoomNet(room, occupancy, arrivalDate, departureDate, contract = belvedereContract) {
  if (room?.pricingKind === "supplement") {
    return calculateSupplementRoomNet(room, occupancy, arrivalDate, departureDate, contract);
  }
  if (room?.pricingKind === "base" || room?.chargeType === "per_adult") {
    return calculateAdultChildRoomNet(room, occupancy, arrivalDate, departureDate, contract);
  }
  if (room.chargeType === "per_person") {
    return calculatePerPersonRoomNet(room, occupancy, arrivalDate, departureDate, contract);
  }

  const nightly = getNightlySeasonBreakdown(arrivalDate, departureDate, contract);
  let total = 0;

  for (const night of nightly) {
    if (!night.season) continue;
    const rate = room.seasonalPrices[night.season];
    total += rate;
  }

  return Math.round(total * 100) / 100;
}

/**
 * Campaign pricing SSOT constants — single source for all channels.
 * Agent hotel: net × 1.10 | Public hotel: net × 1.20 (30% list − 10% sale label).
 * Agent flight: supplier net + pax × 100 ₪ | Public flight: supplier net + pax × 130 ₪.
 */
export const BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT = 10;
export const BELVEDERE_PUBLIC_HOTEL_LIST_MARKUP_PERCENT = 30;
export const BELVEDERE_PUBLIC_HOTEL_EFFECTIVE_MARKUP_TARGET_PERCENT = 20;
export const BELVEDERE_PUBLIC_HOTEL_SALE_DISCOUNT_PERCENT = 10;
/** @deprecated Replaced by list + effective target — kept for legacy imports. */
export const BELVEDERE_PUBLIC_HOTEL_MARKUP_PERCENT = 25;

/** Hidden flight markup (ILS per passenger) — agent vs public channel. */
export const BELVEDERE_FLIGHT_AGENT_HIDDEN_MARKUP_ILS_PER_PAX = 100;
export const BELVEDERE_FLIGHT_PUBLIC_HIDDEN_MARKUP_ILS_PER_PAX = 130;
/** Public flight only: fixed agent commission % on supplier net (on top of the 130 ₪/pax HR fee). */
export const BELVEDERE_FLIGHT_PUBLIC_AGENT_COMMISSION_PERCENT = 10;

/**
 * Campaign passenger count SSOT: adults + children + infants per room.
 * Infants are derived from childAges (0–1.99); when ages are missing, all children count.
 */
export function countBelvedereCampaignPassengers(rooms) {
  return (rooms || []).reduce((sum, room) => {
    const counts = classifyBelvedereCampaignOccupancyPassengers(room);
    return sum + counts.adultCount + counts.childCount + counts.infantCount;
  }, 0);
}

export function classifyBelvedereCampaignOccupancyPassengers(occupancy) {
  const { adults, children, childAges } = normalizeBelvedereCampaignRoomOccupancy(occupancy);

  const hadExplicitAges =
    (Array.isArray(occupancy?.childAges) && occupancy.childAges.length > 0) ||
    Array.isArray(occupancy?.children);

  // Count-only rooms: never invent infants from padded default ages.
  if (!hadExplicitAges) {
    return { adultCount: adults, childCount: children, infantCount: 0 };
  }

  const infantCount = childAges.filter((age) => age >= 0 && age <= INFANT_MAX_AGE).length;
  const childCount = childAges.filter((age) => age > INFANT_MAX_AGE && age <= CHILD_MAX_AGE).length;
  const olderAsAdults = childAges.filter((age) => age > CHILD_MAX_AGE).length;
  return {
    adultCount: adults + olderAsAdults,
    childCount,
    infantCount,
  };
}

export function aggregateBelvedereCampaignPassengerCounts(occupancies) {
  return (occupancies || []).reduce(
    (acc, occupancy) => {
      const counts = classifyBelvedereCampaignOccupancyPassengers(occupancy);
      acc.adultCount += counts.adultCount;
      acc.childCount += counts.childCount;
      acc.infantCount += counts.infantCount;
      return acc;
    },
    { adultCount: 0, childCount: 0, infantCount: 0 },
  );
}

function emptyPassengerTypeHotelTotals() {
  return {
    adults: { count: 0, totalNetEur: 0 },
    children: { count: 0, totalNetEur: 0 },
    infants: { count: 0, totalNetEur: 0 },
  };
}

function mergePassengerTypeHotelTotals(left, right) {
  return {
    adults: {
      count: left.adults.count + right.adults.count,
      totalNetEur: roundEurAmount(left.adults.totalNetEur + right.adults.totalNetEur),
    },
    children: {
      count: left.children.count + right.children.count,
      totalNetEur: roundEurAmount(left.children.totalNetEur + right.children.totalNetEur),
    },
    infants: {
      count: left.infants.count + right.infants.count,
      totalNetEur: roundEurAmount(left.infants.totalNetEur + right.infants.totalNetEur),
    },
  };
}

function roundEurAmount(amount) {
  return Math.round((Number(amount) || 0) * 100) / 100;
}

function resolvePayingChildAgesForHotelPricing(occupancy) {
  const children = Number(occupancy?.children) || 0;
  const childAges = Array.isArray(occupancy?.childAges) ? occupancy.childAges : [];
  if (childAges.length > 0) {
    return childAges.filter((age) => age > INFANT_MAX_AGE && age <= CHILD_MAX_AGE);
  }
  return Array.from({ length: children }, () => 8);
}

function resolveInfantAgesForHotelPricing(occupancy) {
  const childAges = Array.isArray(occupancy?.childAges) ? occupancy.childAges : [];
  if (childAges.length > 0) {
    return childAges.filter((age) => age >= 0 && age <= INFANT_MAX_AGE);
  }
  return [];
}

function computePerPersonRoomHotelNetEurByPassengerType(
  room,
  occupancy,
  arrivalDate,
  departureDate,
  contract = belvedereContract,
) {
  const nightly = getNightlySeasonBreakdown(arrivalDate, departureDate, contract);
  const childAges = Array.isArray(occupancy?.childAges) ? occupancy.childAges : [];
  const olderAsAdults = childAges.filter((age) => age > CHILD_MAX_AGE).length;
  const payingChildAges = resolvePayingChildAgesForHotelPricing(occupancy);
  const infantAges = resolveInfantAgesForHotelPricing(occupancy);
  const adultSlots = (Number(occupancy?.adults) || 0) + olderAsAdults;
  const hasTwoAdults = (Number(occupancy?.adults) || 0) >= 2 && olderAsAdults === 0;

  let adultTotal = 0;
  let childTotal = 0;
  let infantTotal = 0;

  for (const night of nightly) {
    if (!night.season) continue;
    const baseRate = room.seasonalPrices[night.season];
    for (let slot = 1; slot <= adultSlots; slot += 1) {
      adultTotal +=
        (baseRate * resolvePdfAdultSlotChargePercent(room.code, slot, night.season)) / 100;
    }
    for (let i = 0; i < infantAges.length; i += 1) {
      infantTotal += (baseRate * resolvePdfInfantChargePercent(night.season)) / 100;
    }
    for (let childIndex = 1; childIndex <= payingChildAges.length; childIndex += 1) {
      childTotal +=
        (baseRate *
          resolvePdfChildSlotChargePercent(room.code, childIndex, hasTwoAdults, night.season)) /
        100;
    }
  }

  return {
    adults: { count: adultSlots, totalNetEur: roundEurAmount(adultTotal) },
    children: { count: payingChildAges.length, totalNetEur: roundEurAmount(childTotal) },
    infants: { count: infantAges.length, totalNetEur: roundEurAmount(infantTotal) },
  };
}

function computePerUnitRoomHotelNetEurByPassengerType(
  room,
  occupancy,
  arrivalDate,
  departureDate,
  contract = belvedereContract,
) {
  const roomNet = calculateRoomNet(room, occupancy, arrivalDate, departureDate, contract);
  const childAges = Array.isArray(occupancy?.childAges) ? occupancy.childAges : [];
  const olderAsAdults = childAges.filter((age) => age > CHILD_MAX_AGE).length;
  const payingChildAges = resolvePayingChildAgesForHotelPricing(occupancy);
  const infantAges = resolveInfantAgesForHotelPricing(occupancy);
  const adultSlots = (Number(occupancy?.adults) || 0) + olderAsAdults;

  // Unit fare is flat per night — occupancy within limits does not change hotel net.
  // "ילד ראשון חינם" on family units means children do not add hotel cost in the breakdown.
  return {
    adults: { count: adultSlots, totalNetEur: roundEurAmount(roomNet) },
    children: { count: payingChildAges.length, totalNetEur: 0 },
    infants: { count: infantAges.length, totalNetEur: 0 },
  };
}

export function computePackageHotelNetEurByPassengerType({
  roomBreakdown,
  occupancies,
  arrivalDate,
  departureDate,
  hotelNetEur,
  contract = belvedereContract,
}) {
  if (Array.isArray(roomBreakdown) && roomBreakdown.length > 0 && arrivalDate && departureDate) {
    return roomBreakdown.reduce((totals, row, index) => {
      const occupancy =
        row.pricingOccupancy ??
        occupancies?.[index] ??
        occupancies?.[row.roomIndex - 1];
      const room = row.room;
      if (!room || !occupancy || row.compatible === false) return totals;
      const roomTotals =
        room.chargeType === "per_person"
          ? computePerPersonRoomHotelNetEurByPassengerType(
              room,
              occupancy,
              arrivalDate,
              departureDate,
              contract,
            )
          : computePerUnitRoomHotelNetEurByPassengerType(
              room,
              occupancy,
              arrivalDate,
              departureDate,
              contract,
            );
      return mergePassengerTypeHotelTotals(totals, roomTotals);
    }, emptyPassengerTypeHotelTotals());
  }

  const counts = aggregateBelvedereCampaignPassengerCounts(occupancies);
  const totalGuests = counts.adultCount + counts.childCount + counts.infantCount;
  const net = Number(hotelNetEur) || 0;
  if (totalGuests <= 0) {
    return {
      adults: { count: 1, totalNetEur: net },
      children: { count: 0, totalNetEur: 0 },
      infants: { count: 0, totalNetEur: 0 },
    };
  }
  const perGuest = net / totalGuests;
  return {
    adults: { count: counts.adultCount, totalNetEur: roundEurAmount(perGuest * counts.adultCount) },
    children: {
      count: counts.childCount,
      totalNetEur: roundEurAmount(perGuest * counts.childCount),
    },
    infants: {
      count: counts.infantCount,
      totalNetEur: roundEurAmount(perGuest * counts.infantCount),
    },
  };
}

/** Missing/zero supplier fares must fall through to adult (or residual) allocation. */
function positiveFlightFare(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function resolveBelvedereFlightSupplierUsdByPassengerType(flightDeal, occupancies) {
  const counts = aggregateBelvedereCampaignPassengerCounts(occupancies);
  if (!flightDeal) {
    return { adultUsd: 0, childUsd: 0, infantUsd: 0, counts };
  }

  const pricing = buildFlightOfferPricingDisplay(flightDeal);
  const totalFare = positiveFlightFare(pricing.totalFare) ?? 0;
  const totalPax = Math.max(1, counts.adultCount + counts.childCount + counts.infantCount);
  const adultUsd =
    positiveFlightFare(pricing.pricePerPerson) ??
    (totalFare > 0 ? totalFare / totalPax : 0);
  const childUsd = positiveFlightFare(pricing.childFare) ?? adultUsd;
  let infantUsd = positiveFlightFare(pricing.infantFare);

  if (infantUsd == null && counts.infantCount > 0) {
    const allocated =
      adultUsd * counts.adultCount + childUsd * counts.childCount;
    infantUsd = Math.max(0, (totalFare - allocated) / counts.infantCount);
  } else if (infantUsd == null) {
    infantUsd = 0;
  }

  return { adultUsd, childUsd, infantUsd, counts };
}

function reconcileBelvederePassengerPricingLines(draftLines, targetPackageIls) {
  const lines = draftLines
    .filter((line) => line.count > 0)
    .map((line) => {
      const unitPriceIls = roundIlsAmount(line.unitPriceIls);
      return {
        ...line,
        unitPriceIls,
        lineTotalIls: roundIlsAmount(unitPriceIls * line.count),
      };
    });

  let packageTotalIls = roundIlsAmount(lines.reduce((sum, line) => sum + line.lineTotalIls, 0));
  const diff = roundIlsAmount((Number(targetPackageIls) || 0) - packageTotalIls);
  if (diff !== 0 && lines.length > 0) {
    const adjustIndex = Math.max(0, lines.findIndex((line) => line.type === "adult"));
    const line = lines[adjustIndex];
    line.lineTotalIls = roundIlsAmount(line.lineTotalIls + diff);
    line.unitPriceIls = roundIlsAmount(line.lineTotalIls / line.count);
    packageTotalIls = roundIlsAmount(lines.reduce((sum, row) => sum + row.lineTotalIls, 0));
  }

  return {
    lines,
    packageTotalIls,
    adultUnitPriceIls: lines.find((line) => line.type === "adult")?.unitPriceIls ?? null,
    childUnitPriceIls: lines.find((line) => line.type === "child")?.unitPriceIls ?? null,
    infantUnitPriceIls: lines.find((line) => line.type === "infant")?.unitPriceIls ?? null,
  };
}

/** Re-target passenger lines so Σ(line totals) equals `targetPackageIls`. */
export function retargetBelvederePassengerPricing(passengerPricing, targetPackageIls) {
  if (!passengerPricing?.lines?.length) return passengerPricing ?? null;
  return reconcileBelvederePassengerPricingLines(
    passengerPricing.lines.map((line) => ({
      type: line.type,
      labelHe: line.labelHe,
      count: line.count,
      unitPriceIls: line.unitPriceIls,
    })),
    targetPackageIls,
  );
}

function sumBelvederePassengerLineTotalsIls(passengerPricing) {
  if (!passengerPricing?.lines?.length) return 0;
  const fromLines = passengerPricing.lines.reduce(
    (sum, line) => sum + (Number(line.lineTotalIls) || 0),
    0,
  );
  if (fromLines > 0) return roundIlsAmount(fromLines);
  return roundIlsAmount(Number(passengerPricing.packageTotalIls) || 0);
}

/**
 * Customer-facing passenger breakdown must include agent-added commission so
 * Σ(lines) matches the package portion of finalSalePriceIls (excl. baggage).
 * Agent/staff UI keeps net `passengerPricing` + a separate commission row.
 *
 * Important: do NOT trust persisted `customer_passenger_pricing` blindly —
 * older/partial saves often stored a net copy via `?? passengerPricing`.
 * Only reuse it when Σ(lines) already matches the customer package target.
 */
export function resolveBelvedereCustomerPassengerPricing({
  customerPassengerPricing = null,
  passengerPricing = null,
  agentAddedCommissionIls = 0,
  /** Optional: finalSalePriceIls - baggageExtras (preferred target). */
  customerPackageIls = null,
} = {}) {
  const netSource = passengerPricing?.lines?.length
    ? passengerPricing
    : customerPassengerPricing?.lines?.length
      ? customerPassengerPricing
      : null;
  if (!netSource) return passengerPricing ?? customerPassengerPricing ?? null;

  const netTotalIls = sumBelvederePassengerLineTotalsIls(
    passengerPricing?.lines?.length ? passengerPricing : netSource,
  );
  const commissionIls = roundIlsAmount(Number(agentAddedCommissionIls) || 0);
  const explicitTarget =
    customerPackageIls != null && Number.isFinite(Number(customerPackageIls))
      ? roundIlsAmount(Number(customerPackageIls))
      : null;
  const targetPackageIls =
    explicitTarget != null && explicitTarget > 0
      ? explicitTarget
      : roundIlsAmount(netTotalIls + commissionIls);

  if (customerPassengerPricing?.lines?.length) {
    const customerTotalIls = sumBelvederePassengerLineTotalsIls(customerPassengerPricing);
    if (targetPackageIls > 0 && customerTotalIls === targetPackageIls) {
      return customerPassengerPricing;
    }
  }

  if (targetPackageIls <= 0 || targetPackageIls === netTotalIls) {
    return passengerPricing?.lines?.length ? passengerPricing : netSource;
  }

  // Always retarget from net lines when available so we never double-count
  // commission that may already sit in a stale customer breakdown.
  const retargetSource = passengerPricing?.lines?.length
    ? passengerPricing
    : customerPassengerPricing;
  return retargetBelvederePassengerPricing(retargetSource, targetPackageIls);
}

export function buildBelvedereCampaignPassengerPricing({
  hotelNetEur,
  channelFinalPriceIls,
  eurToIls,
  usdToIls,
  agentChannel = false,
  publicChannel = false,
  occupancies = [],
  roomBreakdown = null,
  arrivalDate = null,
  departureDate = null,
  flightDeal = null,
  contract = belvedereContract,
}) {
  const eurRate = Number(eurToIls);
  const usdRate = Number(usdToIls);
  if (!Number.isFinite(eurRate) || eurRate <= 0) return null;

  const hotelMarkupFactor = agentChannel
    ? 1 + BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT / 100
    : 1 + resolveBelvederePublicHotelEffectiveMarkupPercent(contract) / 100;
  const hiddenPerPaxIls = resolveBelvedereFlightHiddenMarkupIlsPerPax({ agentChannel, publicChannel });
  const flightCommissionFactor =
    1 + resolveBelvedereFlightAgentCommissionPercent({ agentChannel, publicChannel }) / 100;

  const hotelByType = computePackageHotelNetEurByPassengerType({
    roomBreakdown,
    occupancies,
    arrivalDate,
    departureDate,
    hotelNetEur,
    contract,
  });

  const hotelChannelIlsByType = {
    adults: roundIlsAmount(hotelByType.adults.totalNetEur * eurRate * hotelMarkupFactor),
    children: roundIlsAmount(hotelByType.children.totalNetEur * eurRate * hotelMarkupFactor),
    infants: roundIlsAmount(hotelByType.infants.totalNetEur * eurRate * hotelMarkupFactor),
  };

  const flightUsdByType = resolveBelvedereFlightSupplierUsdByPassengerType(flightDeal, occupancies);
  const hasFlight = Boolean(flightDeal) && Number.isFinite(usdRate) && usdRate > 0;
  const flightUnitIlsByType = hasFlight
    ? {
        adult: roundIlsAmount(
          flightUsdByType.adultUsd * usdRate * flightCommissionFactor + hiddenPerPaxIls,
        ),
        child: roundIlsAmount(
          flightUsdByType.childUsd * usdRate * flightCommissionFactor + hiddenPerPaxIls,
        ),
        infant: roundIlsAmount(
          flightUsdByType.infantUsd * usdRate * flightCommissionFactor + hiddenPerPaxIls,
        ),
      }
    : { adult: 0, child: 0, infant: 0 };

  const draftLines = [];
  if (hotelByType.adults.count > 0) {
    draftLines.push({
      type: "adult",
      labelHe: "מבוגר",
      count: hotelByType.adults.count,
      unitPriceIls:
        hotelChannelIlsByType.adults / hotelByType.adults.count + flightUnitIlsByType.adult,
    });
  }
  if (hotelByType.children.count > 0) {
    const hotelShareIls =
      hotelByType.children.totalNetEur > 0
        ? hotelChannelIlsByType.children / hotelByType.children.count
        : 0;
    const childUnitPriceIls = hotelShareIls + flightUnitIlsByType.child;
    if (childUnitPriceIls > 0) {
      draftLines.push({
        type: "child",
        labelHe:
          hotelShareIls <= 0 && flightUnitIlsByType.child > 0 ? "ילד (טיסה)" : "ילד",
        count: hotelByType.children.count,
        unitPriceIls: childUnitPriceIls,
      });
    }
  }
  if (hotelByType.infants.count > 0) {
    const infantUnitPriceIls =
      (hotelByType.infants.totalNetEur > 0
        ? hotelChannelIlsByType.infants / hotelByType.infants.count
        : 0) + flightUnitIlsByType.infant;
    if (infantUnitPriceIls > 0) {
      draftLines.push({
        type: "infant",
        labelHe: "תינוק",
        count: hotelByType.infants.count,
        unitPriceIls: infantUnitPriceIls,
      });
    }
  }

  return reconcileBelvederePassengerPricingLines(draftLines, channelFinalPriceIls);
}

export function resolveBelvederePublicHotelSaleDiscountPercent(
  contract = belvedereContract,
) {
  if (contract.publicSaleDiscountPercent != null) {
    return Number(contract.publicSaleDiscountPercent) || 0;
  }
  const listPct =
    contract.publicListMarkupPercent ?? BELVEDERE_PUBLIC_HOTEL_LIST_MARKUP_PERCENT;
  const effectiveTarget =
    contract.publicEffectiveMarkupTargetPercent ??
    BELVEDERE_PUBLIC_HOTEL_EFFECTIVE_MARKUP_TARGET_PERCENT;
  const listFactor = 1 + listPct / 100;
  const targetFactor = 1 + effectiveTarget / 100;
  return Math.round((1 - targetFactor / listFactor) * 10000) / 100;
}

export function resolveBelvederePublicHotelEffectiveMarkupPercent(
  contract = belvedereContract,
) {
  return (
    contract.publicEffectiveMarkupTargetPercent ??
    BELVEDERE_PUBLIC_HOTEL_EFFECTIVE_MARKUP_TARGET_PERCENT
  );
}

/**
 * Public end-customer hotel price: net × 1.20 sale; list net × 1.30 for strikethrough UI.
 */
export function calculateBelvederePublicHotelSalePriceEur(
  hotelNetEur,
  contract = belvedereContract,
) {
  const net = Number(hotelNetEur) || 0;
  const listMarkupPercent =
    contract.publicListMarkupPercent ?? BELVEDERE_PUBLIC_HOTEL_LIST_MARKUP_PERCENT;
  const effectiveTarget = resolveBelvederePublicHotelEffectiveMarkupPercent(contract);
  const saleDiscountPercent = resolveBelvederePublicHotelSaleDiscountPercent(contract);

  if (net <= 0) {
    return {
      hotelNetEur: 0,
      listPriceEur: 0,
      salePriceEur: 0,
      hotelMarkupEur: 0,
      listMarkupPercent,
      saleDiscountPercent,
      effectiveMarkupPercent: 0,
    };
  }

  const listPriceEur = Math.round(net * (1 + listMarkupPercent / 100) * 100) / 100;
  const salePriceEur = Math.round(net * (1 + effectiveTarget / 100) * 100) / 100;
  const hotelMarkupEur = Math.round((salePriceEur - net) * 100) / 100;
  const effectiveMarkupPercent = Math.round((hotelMarkupEur / net) * 10000) / 100;

  return {
    hotelNetEur: net,
    listPriceEur,
    salePriceEur,
    hotelMarkupEur,
    listMarkupPercent,
    saleDiscountPercent,
    effectiveMarkupPercent,
  };
}

export function resolveHotelMarkupPercent(
  hotelNet,
  contract = belvedereContract,
  { agentChannel = false } = {},
) {
  void hotelNet;
  if (agentChannel) {
    return contract.agentMarkupPercent ?? BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT;
  }
  return resolveBelvederePublicHotelEffectiveMarkupPercent(contract);
}

/**
 * Hotel markup channel for campaign UI — follows view mode, not session agent_id.
 * Customer preview (DEV VIEW customer or public) → public markup; agent view → agent markup.
 */
export function isBelvedereAgentPricingChannel(entryContext, { agentViewMode = false } = {}) {
  void entryContext;
  return Boolean(agentViewMode);
}

/**
 * Agent commission: 10% of hotel net (= half of a 20% markup pool), not 10% of markup €.
 */
export const BELVEDERE_AGENT_COMMISSION_NET_PERCENT = 10;

export function calculateAgentHotelCommission(hotelNetEur) {
  const net = Number(hotelNetEur) || 0;
  if (net <= 0) return 0;
  return (
    Math.round(net * (BELVEDERE_AGENT_COMMISSION_NET_PERCENT / 100) * 100) / 100
  );
}

/** @deprecated Use calculateAgentHotelCommission — kept for import stability */
export const calculateAgentHotelMarkupShare = calculateAgentHotelCommission;

export function agentCommissionShareOfMarkupPercent(markupPercent) {
  const markup = Number(markupPercent) || BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT;
  if (markup <= 0) return 0;
  return BELVEDERE_AGENT_COMMISSION_NET_PERCENT / markup;
}

export function formatAgentCommissionLabel(markupPercent) {
  const mp = Number(markupPercent) || BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT;
  return `מרווח סוכן (${BELVEDERE_AGENT_COMMISSION_NET_PERCENT}% מנטו — מתוך מרק־אפ ${mp}%)`;
}

export function formatAgentHotelCommissionDisplay(hotelNetEur, eurRate) {
  const shareEur = calculateAgentHotelCommission(hotelNetEur);
  if (shareEur <= 0) return "—";
  const rate = Number(eurRate);
  if (!Number.isFinite(rate) || rate <= 0) return formatEur(shareEur);
  const ils = Math.round(shareEur * rate);
  return `${formatIls(ils)} (${formatEur(shareEur)})`;
}

function roundIlsAmount(amount) {
  return Math.round((Number(amount) || 0) * 100) / 100;
}

function resolveChannelHotelMarkup(
  hotelNetEur,
  hotelNetIls,
  { agentChannel = false, publicChannel = false, contract = null } = {},
) {
  if (agentChannel) {
    const fromContract = Number(contract?.agentMarkupPercent);
    const pct = Number.isFinite(fromContract)
      ? fromContract
      : BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT;
    const hotelMarkupEur = Math.round(hotelNetEur * (pct / 100) * 100) / 100;
    return {
      hotelMarkupEur,
      hotelMarkupIls: roundIlsAmount(hotelNetIls * (pct / 100)),
      hotelMarkupPercent: pct,
    };
  }
  if (publicChannel) {
    const sale = calculateBelvederePublicHotelSalePriceEur(hotelNetEur, contract || undefined);
    const markupRatio = hotelNetEur > 0 ? sale.hotelMarkupEur / hotelNetEur : 0;
    return {
      hotelMarkupEur: sale.hotelMarkupEur,
      hotelMarkupIls: roundIlsAmount(hotelNetIls * markupRatio),
      hotelMarkupPercent: sale.effectiveMarkupPercent,
      listPriceEur: sale.listPriceEur,
      saleDiscountPercent: sale.saleDiscountPercent,
      listMarkupPercent: sale.listMarkupPercent,
    };
  }
  return null;
}

/**
 * Campaign pricing SSOT (ILS display layer).
 *
 * channel_final_price = hotel_net + hotel_markup + flight_channel_price
 * flight_channel_price (agent)  = flight_supplier_net + 100 ₪/pax
 * flight_channel_price (public) = flight_supplier_net + 130 ₪/pax (HR fee) + 10% fixed agent commission on net
 * final_sale_price = channel_final_price + baggage_extras
 */

export function resolveBelvedereFlightHiddenMarkupIlsPerPax({
  agentChannel = false,
  publicChannel = false,
} = {}) {
  if (agentChannel) return BELVEDERE_FLIGHT_AGENT_HIDDEN_MARKUP_ILS_PER_PAX;
  if (publicChannel) return BELVEDERE_FLIGHT_PUBLIC_HIDDEN_MARKUP_ILS_PER_PAX;
  return 0;
}

export function calculateBelvedereFlightHiddenMarkupIls(
  totalPax,
  { agentChannel = false, publicChannel = false } = {},
) {
  const perPax = resolveBelvedereFlightHiddenMarkupIlsPerPax({ agentChannel, publicChannel });
  const pax = Math.max(0, Math.floor(Number(totalPax)) || 0);
  if (!perPax || !pax) return 0;
  return roundIlsAmount(perPax * pax);
}

/** Public channel flight: fixed agent commission % applied to supplier net. */
export function resolveBelvedereFlightAgentCommissionPercent({
  agentChannel = false,
  publicChannel = false,
} = {}) {
  if (!agentChannel && publicChannel) return BELVEDERE_FLIGHT_PUBLIC_AGENT_COMMISSION_PERCENT;
  return 0;
}

export function resolveBelvedereFlightIlsFromBreakdown(
  flightNetUsd,
  usdToIls,
  totalPax,
  { agentChannel = false, publicChannel = false } = {},
) {
  const usdRate = Number(usdToIls);
  const netUsd = Number(flightNetUsd) || 0;
  if (netUsd <= 0 || !Number.isFinite(usdRate) || usdRate <= 0) {
    return {
      flightSupplierNetIls: 0,
      flightHiddenMarkupIls: 0,
      flightHiddenMarkupPerPaxIls: 0,
      flightAgentCommissionPercent: 0,
      flightAgentCommissionIls: 0,
      flightNetIls: 0,
      flightNetUsd: 0,
      hasFlight: false,
    };
  }

  const flightSupplierNetIls = roundIlsAmount(netUsd * usdRate);
  const flightHiddenMarkupPerPaxIls = resolveBelvedereFlightHiddenMarkupIlsPerPax({
    agentChannel,
    publicChannel,
  });
  const flightHiddenMarkupIls = calculateBelvedereFlightHiddenMarkupIls(totalPax, {
    agentChannel,
    publicChannel,
  });
  const flightAgentCommissionPercent = resolveBelvedereFlightAgentCommissionPercent({
    agentChannel,
    publicChannel,
  });
  const flightAgentCommissionIls = roundIlsAmount(
    flightSupplierNetIls * (flightAgentCommissionPercent / 100),
  );
  const flightNetIls = roundIlsAmount(
    flightSupplierNetIls + flightHiddenMarkupIls + flightAgentCommissionIls,
  );

  return {
    flightSupplierNetIls,
    flightHiddenMarkupIls,
    flightHiddenMarkupPerPaxIls,
    flightAgentCommissionPercent,
    flightAgentCommissionIls,
    flightNetIls,
    flightNetUsd: netUsd,
    hasFlight: true,
  };
}

export function buildBelvedereCampaignPricingSsot(
  breakdown,
  {
    eurToIls,
    usdToIls,
    agentAddedCommissionMode = BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_PERCENT,
    agentAddedCommissionPercent = 0,
    agentAddedCommissionAmountIls = 0,
    agentChannel = false,
    publicChannel = false,
    totalPax = 1,
    baggageExtrasTotalIls = 0,
    occupancies = null,
    roomBreakdown = null,
    arrivalDate = null,
    departureDate = null,
    flightDeal = null,
    contract = null,
  } = {},
) {
  if (!breakdown) return null;

  const eurRate = Number(eurToIls);
  const usdRate = Number(usdToIls);
  if (!Number.isFinite(eurRate) || eurRate <= 0) return null;

  const hotelNetEur = Number(breakdown.hotelNet) || 0;
  const hotelNetIls = roundIlsAmount(hotelNetEur * eurRate);
  const channelMarkup = resolveChannelHotelMarkup(hotelNetEur, hotelNetIls, {
    agentChannel,
    publicChannel,
    contract,
  });
  const hotelMarkupEur = channelMarkup
    ? channelMarkup.hotelMarkupEur
    : breakdown.hotelMarkup != null
      ? Number(breakdown.hotelMarkup) || 0
      : Math.max(0, (Number(breakdown.hotelTotal) || 0) - hotelNetEur);
  const hotelMarkupIls = channelMarkup
    ? channelMarkup.hotelMarkupIls
    : roundIlsAmount(hotelMarkupEur * eurRate);
  const hotelMarkupPercent = channelMarkup
    ? channelMarkup.hotelMarkupPercent
    : breakdown.markupPercent != null
      ? Number(breakdown.markupPercent) || 0
      : hotelNetEur > 0
        ? Math.round((hotelMarkupEur / hotelNetEur) * 10000) / 100
        : 0;

  let flightNetIls = 0;
  let flightSupplierNetIls = 0;
  let flightHiddenMarkupIls = 0;
  let flightHiddenMarkupPerPaxIls = 0;
  let flightAgentCommissionPercent = 0;
  let flightAgentCommissionIls = 0;
  let flightNetUsd = 0;
  let hasFlight = false;
  const flightNetUsdRaw = breakdown.flightNet ?? breakdown.flightTotal;
  if (flightNetUsdRaw != null) {
    const flightPricing = resolveBelvedereFlightIlsFromBreakdown(
      flightNetUsdRaw,
      usdRate,
      totalPax,
      { agentChannel, publicChannel },
    );
    flightNetIls = flightPricing.flightNetIls;
    flightSupplierNetIls = flightPricing.flightSupplierNetIls;
    flightHiddenMarkupIls = flightPricing.flightHiddenMarkupIls;
    flightHiddenMarkupPerPaxIls = flightPricing.flightHiddenMarkupPerPaxIls;
    flightAgentCommissionPercent = flightPricing.flightAgentCommissionPercent;
    flightAgentCommissionIls = flightPricing.flightAgentCommissionIls;
    flightNetUsd = flightPricing.flightNetUsd;
    hasFlight = flightPricing.hasFlight;
  }

  const channelFinalPriceIls = roundIlsAmount(hotelNetIls + flightNetIls + hotelMarkupIls);
  const internalAgentMarginIls = 0;
  const resolvedCommission = resolveBelvedereCampaignAgentAddedCommission(channelFinalPriceIls, {
    mode: agentAddedCommissionMode,
    percent: agentAddedCommissionPercent,
    amountIls: agentAddedCommissionAmountIls,
  });
  const agentAddedCommissionIls = resolvedCommission.amountIls;
  const baggageExtrasIls = roundIlsAmount(Number(baggageExtrasTotalIls) || 0);
  const finalSalePriceIls = roundIlsAmount(
    channelFinalPriceIls + agentAddedCommissionIls + baggageExtrasIls,
  );
  const hotelAgentPriceIls = roundIlsAmount(hotelNetIls + hotelMarkupIls);
  const hotelAgentPriceEur = Math.round((hotelNetEur + hotelMarkupEur) * 100) / 100;
  const agentNetPriceIls = channelFinalPriceIls;
  const agentNetPriceEur = Math.round((agentNetPriceIls / eurRate) * 100) / 100;
  const agentAddedCommissionEur = Math.round((agentAddedCommissionIls / eurRate) * 100) / 100;
  const finalSalePriceEur = Math.round((finalSalePriceIls / eurRate) * 100) / 100;

  const passengerPricing = buildBelvedereCampaignPassengerPricing({
    hotelNetEur,
    channelFinalPriceIls,
    eurToIls: eurRate,
    usdToIls: usdRate,
    agentChannel,
    publicChannel,
    occupancies: occupancies ?? [],
    roomBreakdown,
    arrivalDate,
    departureDate,
    flightDeal,
  });

  const customerPackageIls = roundIlsAmount(channelFinalPriceIls + agentAddedCommissionIls);
  const customerPassengerPricing =
    agentAddedCommissionIls > 0 && passengerPricing
      ? retargetBelvederePassengerPricing(passengerPricing, customerPackageIls)
      : passengerPricing;

  return {
    hotelNetIls,
    hotelNetEur,
    flightNetIls,
    flightSupplierNetIls,
    flightHiddenMarkupIls,
    flightHiddenMarkupPerPaxIls,
    flightAgentCommissionPercent,
    flightAgentCommissionIls,
    flightNetUsd,
    hotelMarkupIls,
    hotelMarkupEur,
    hotelMarkupPercent,
    agentNetPriceIls,
    agentNetPriceEur,
    internalAgentMarginIls,
    agentAddedCommissionMode: resolvedCommission.mode,
    agentAddedCommissionPercent: resolvedCommission.percent,
    agentAddedCommissionIls,
    agentAddedCommissionEur,
    baggageExtrasTotalIls: baggageExtrasIls,
    finalSalePriceIls,
    finalSalePriceEur,
    hasFlight,
    hotelSellingIls: hotelAgentPriceIls,
    hotelAgentPriceIls,
    hotelAgentPriceEur,
    passengerPricing,
    customerPassengerPricing,
    adultUnitPriceIls: passengerPricing?.adultUnitPriceIls ?? null,
    childUnitPriceIls: passengerPricing?.childUnitPriceIls ?? null,
    infantUnitPriceIls: passengerPricing?.infantUnitPriceIls ?? null,
  };
}

/** Hotel-only agent price (net + markup) — same value shown for hotel-only search. */
export function resolveBelvedereHotelAgentPriceIls(pricingSsot) {
  if (!pricingSsot) return 0;
  return (
    pricingSsot.hotelAgentPriceIls ??
    pricingSsot.hotelSellingIls ??
    roundIlsAmount((Number(pricingSsot.hotelNetIls) || 0) + (Number(pricingSsot.hotelMarkupIls) || 0))
  );
}

/** Base for agent commission % display — hotel price only when package is split for agents. */
export function resolveBelvedereAgentCommissionDisplayBaseIls(pricingSsot, { splitHotelAndFlight = false } = {}) {
  if (!pricingSsot) return 0;
  if (splitHotelAndFlight && pricingSsot.hasFlight) {
    return resolveBelvedereHotelAgentPriceIls(pricingSsot);
  }
  return Number(pricingSsot.agentNetPriceIls) || 0;
}

export function resolveBelvedereCampaignAgentAddedCommissionPercent(
  agentAddedCommissionPercent,
  { entryContext } = {},
) {
  if (!entryContext?.agent_id) return 0;
  return normalizeBelvedereCampaignAgentAddedCommissionPercent(agentAddedCommissionPercent);
}

/** @deprecated Use resolveBelvedereCampaignAgentAddedCommissionPercent */
export function resolveBelvedereCampaignAgentMarkupPercent(
  agentAddedCommissionPercent,
  options = {},
) {
  return resolveBelvedereCampaignAgentAddedCommissionPercent(agentAddedCommissionPercent, options);
}

/**
 * @deprecated Use buildBelvedereCampaignPricingSsot — kept for import stability.
 */
export function buildBelvedereAdminIlsSummary(breakdown, options = {}) {
  return buildBelvedereCampaignPricingSsot(breakdown, options);
}

/** @deprecated Internal margin removed — agent adds commission via picker. */
export const BELVEDERE_INTERNAL_AGENT_MARGIN_PERCENT = 0;

/** Optional extra commission the agent adds on top of agent net price. */
export const BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_DEFAULT_PERCENT = 0;
export const BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MAX_PERCENT = 50;
export const BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_PRESETS = Object.freeze([0, 2, 5, 10]);
export const BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_PERCENT = "percent";
export const BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_AMOUNT = "amount";
export const BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_AMOUNT_PRESETS = Object.freeze([
  0, 100, 200, 500,
]);

/** @deprecated Use BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_* */
export const BELVEDERE_CAMPAIGN_AGENT_COMMISSION_DEFAULT_PERCENT =
  BELVEDERE_INTERNAL_AGENT_MARGIN_PERCENT;
export const BELVEDERE_CAMPAIGN_AGENT_COMMISSION_PRESETS =
  BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_PRESETS;

export function normalizeBelvedereCampaignAgentAddedCommissionPercent(percent) {
  if (percent === "" || percent == null) return 0;
  const value = Number(percent);
  if (!Number.isFinite(value) || value < 0) return 0;
  const clamped = Math.min(BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MAX_PERCENT, value);
  return Math.round(clamped * 100) / 100;
}

export function normalizeBelvedereCampaignAgentAddedCommissionMode(mode) {
  return mode === BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_AMOUNT
    ? BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_AMOUNT
    : BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_PERCENT;
}

export function resolveBelvedereCampaignAgentAddedCommissionMaxAmountIls(agentNetPriceIls) {
  const base = Number(agentNetPriceIls) || 0;
  if (base <= 0) return 0;
  return roundIlsAmount((base * BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MAX_PERCENT) / 100);
}

export function normalizeBelvedereCampaignAgentAddedCommissionAmountInput(amountIls) {
  if (amountIls === "" || amountIls == null) return 0;
  const value = Number(amountIls);
  if (!Number.isFinite(value) || value < 0) return 0;
  return roundIlsAmount(value);
}

export function normalizeBelvedereCampaignAgentAddedCommissionAmountIls(amountIls, agentNetPriceIls) {
  if (amountIls === "" || amountIls == null) return 0;
  const value = Number(amountIls);
  if (!Number.isFinite(value) || value < 0) return 0;
  const maxAmount = resolveBelvedereCampaignAgentAddedCommissionMaxAmountIls(agentNetPriceIls);
  return roundIlsAmount(Math.min(maxAmount, value));
}

export function resolveBelvedereCampaignAgentAddedCommission(
  agentNetPriceIls,
  {
    mode = BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_PERCENT,
    percent = 0,
    amountIls = 0,
  } = {},
) {
  const base = Number(agentNetPriceIls) || 0;
  const resolvedMode = normalizeBelvedereCampaignAgentAddedCommissionMode(mode);

  if (resolvedMode === BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_AMOUNT) {
    const normalizedAmount = normalizeBelvedereCampaignAgentAddedCommissionAmountIls(
      amountIls,
      base,
    );
    const equivalentPercent =
      base > 0 ? Math.round((normalizedAmount / base) * 10000) / 100 : 0;
    return {
      mode: resolvedMode,
      percent: equivalentPercent,
      amountIls: normalizedAmount,
    };
  }

  const normalizedPercent = normalizeBelvedereCampaignAgentAddedCommissionPercent(percent);
  return {
    mode: BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_PERCENT,
    percent: normalizedPercent,
    amountIls: calculateBelvedereCampaignAgentAddedCommissionIls(base, normalizedPercent),
  };
}

export function resolveBelvedereCampaignAgentAddedCommissionForEntry(
  { mode, percent, amountIls, agentNetPriceIls = null } = {},
  { entryContext } = {},
) {
  if (!entryContext?.agent_id) {
    return resolveBelvedereCampaignAgentAddedCommission(0, {
      mode: BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_PERCENT,
      percent: 0,
      amountIls: 0,
    });
  }
  const knownNet = Number(agentNetPriceIls);
  // When agent net is not known yet, pass raw inputs through so SSOT can resolve against channel price.
  if (!Number.isFinite(knownNet) || knownNet <= 0) {
    return {
      mode: normalizeBelvedereCampaignAgentAddedCommissionMode(mode),
      percent: normalizeBelvedereCampaignAgentAddedCommissionPercent(percent),
      amountIls: normalizeBelvedereCampaignAgentAddedCommissionAmountInput(amountIls),
    };
  }
  return resolveBelvedereCampaignAgentAddedCommission(knownNet, {
    mode,
    percent,
    amountIls,
  });
}

/** @deprecated Use normalizeBelvedereCampaignAgentAddedCommissionPercent */
export function normalizeBelvedereCampaignAgentCommissionPercent(percent) {
  return normalizeBelvedereCampaignAgentAddedCommissionPercent(percent);
}

export function calculateBelvedereInternalAgentMarginIls(hotelNetIls) {
  const net = Number(hotelNetIls) || 0;
  if (net <= 0) return 0;
  return roundIlsAmount((net * BELVEDERE_INTERNAL_AGENT_MARGIN_PERCENT) / 100);
}

export function calculateBelvedereCampaignAgentAddedCommissionIls(agentNetPriceIls, percent) {
  const base = Number(agentNetPriceIls) || 0;
  const pct = Number(percent) || 0;
  if (base <= 0 || pct <= 0) return 0;
  return roundIlsAmount((base * pct) / 100);
}

/** @deprecated Use calculateBelvedereCampaignAgentAddedCommissionIls */
export function calculateBelvedereCampaignAgentCommissionIls(hotelNetIls, commissionPercent) {
  return calculateBelvedereInternalAgentMarginIls(hotelNetIls);
}

export function calculateBelvedereAgentCommissionPercentOfAgentPrice(
  internalMarginIls,
  agentNetPriceIls,
) {
  const margin = Number(internalMarginIls) || 0;
  const agentNet = Number(agentNetPriceIls) || 0;
  if (margin <= 0 || agentNet <= 0) return 0;
  return Math.round((margin / agentNet) * 10000) / 100;
}

export function roundBelvedereAgentCommissionPercentOfAgentPrice(percent) {
  const value = Number(percent) || 0;
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round(value);
}

export function buildBelvedereAgentCommissionAgentViewSummary(agentNetPriceIls, internalMarginIls) {
  const rawPercent = calculateBelvedereAgentCommissionPercentOfAgentPrice(
    internalMarginIls,
    agentNetPriceIls,
  );
  const percent = roundBelvedereAgentCommissionPercentOfAgentPrice(rawPercent);
  return {
    percent,
    amountIls: calculateBelvedereCampaignAgentAddedCommissionIls(agentNetPriceIls, percent),
  };
}

export function formatBelvedereCommissionPercentDisplay(percent) {
  const value = Number(percent) || 0;
  if (!Number.isFinite(value)) return "0";
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
}

export function buildBelvedereInternalAgentMarginSummary(hotelNetIls, agentNetPriceIls) {
  const amountIls = calculateBelvedereInternalAgentMarginIls(hotelNetIls);
  const percentOfHotelNet = BELVEDERE_INTERNAL_AGENT_MARGIN_PERCENT;
  const percentOfAgentPrice = calculateBelvedereAgentCommissionPercentOfAgentPrice(
    amountIls,
    agentNetPriceIls,
  );
  const agentView = buildBelvedereAgentCommissionAgentViewSummary(agentNetPriceIls, amountIls);
  return {
    percentOfHotelNet,
    percentOfAgentPrice,
    agentView,
    percent: percentOfHotelNet,
    amountIls,
  };
}

export function buildBelvedereCampaignAgentAddedCommissionSummary(agentNetPriceIls, commissionInput) {
  const options =
    typeof commissionInput === "number" || commissionInput === "" || commissionInput == null
      ? {
          mode: BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_PERCENT,
          percent: commissionInput,
        }
      : commissionInput;
  return resolveBelvedereCampaignAgentAddedCommission(agentNetPriceIls, options);
}

/** @deprecated Use buildBelvedereCampaignAgentAddedCommissionSummary */
export function buildBelvedereCampaignAgentCommissionSummary(hotelNetIls, commissionPercent) {
  return buildBelvedereInternalAgentMarginSummary(hotelNetIls);
}

export function buildBelvedereCampaignPricingSsotPayload(pricingSsot) {
  if (!pricingSsot) return null;
  const baggageExtrasIls = pricingSsot.baggageExtrasTotalIls ?? 0;
  const customerPackageIls = roundIlsAmount(
    (Number(pricingSsot.finalSalePriceIls) || 0) - (Number(baggageExtrasIls) || 0),
  );
  const customerPassengerPricing = resolveBelvedereCustomerPassengerPricing({
    customerPassengerPricing: pricingSsot.customerPassengerPricing || null,
    passengerPricing: pricingSsot.passengerPricing || null,
    agentAddedCommissionIls: pricingSsot.agentAddedCommissionIls ?? 0,
    customerPackageIls,
  });
  return {
    hotel_net_ils: pricingSsot.hotelNetIls,
    hotel_net_eur: pricingSsot.hotelNetEur,
    eur_to_ils: pricingSsot.eurToIls,
    flight_net_ils: pricingSsot.flightNetIls,
    flight_supplier_net_ils: pricingSsot.flightSupplierNetIls,
    flight_hidden_markup_ils: pricingSsot.flightHiddenMarkupIls,
    flight_hidden_markup_per_pax_ils: pricingSsot.flightHiddenMarkupPerPaxIls,
    flight_agent_commission_percent: pricingSsot.flightAgentCommissionPercent ?? 0,
    flight_agent_commission_ils: pricingSsot.flightAgentCommissionIls ?? 0,
    hotel_markup_ils: pricingSsot.hotelMarkupIls,
    hotel_markup_percent: pricingSsot.hotelMarkupPercent,
    agent_net_price_ils: pricingSsot.agentNetPriceIls,
    internal_agent_margin_percent: 0,
    internal_agent_margin_ils: 0,
    agent_added_commission_mode:
      pricingSsot.agentAddedCommissionMode ?? BELVEDERE_CAMPAIGN_AGENT_ADDED_COMMISSION_MODE_PERCENT,
    agent_added_commission_percent: pricingSsot.agentAddedCommissionPercent ?? 0,
    agent_added_commission_ils: pricingSsot.agentAddedCommissionIls ?? 0,
    total_agent_commission_ils: roundIlsAmount(
      (Number(pricingSsot.flightAgentCommissionIls) || 0) +
        (Number(pricingSsot.agentAddedCommissionIls) || 0),
    ),
    baggage_extras_total_ils: baggageExtrasIls,
    final_sale_price_ils: pricingSsot.finalSalePriceIls,
    adult_unit_price_ils: pricingSsot.adultUnitPriceIls,
    child_unit_price_ils: pricingSsot.childUnitPriceIls,
    infant_unit_price_ils: pricingSsot.infantUnitPriceIls,
    passenger_pricing: pricingSsot.passengerPricing,
    customer_passenger_pricing: customerPassengerPricing,
  };
}

/** @deprecated Use formatAgentHotelCommissionDisplay */
export function formatAgentHotelMarkupShareLabel(hotelNetEur, eurRate) {
  return formatAgentHotelCommissionDisplay(hotelNetEur, eurRate);
}

export function buildPriceBreakdown(
  hotelNet,
  roomCount,
  nights,
  markupPercent,
  contract = belvedereContract,
  pricingOptions = {},
) {
  const { agentChannel = false, publicChannel = false } = pricingOptions;
  const contractCurrency =
    String(contract?.currency || "EUR").toUpperCase() === "USD" ? "USD" : "EUR";

  const accommodationTax =
    Math.round(roomCount * nights * (contract?.accommodationTaxPerRoomPerNight || 0) * 100) / 100;

  if (markupPercent != null) {
    const hotelMarkup = Math.round(hotelNet * (markupPercent / 100) * 100) / 100;
    const hotelTotal = Math.round((hotelNet + hotelMarkup) * 100) / 100;
    return {
      hotelNet,
      hotelMarkup,
      hotelTotal,
      accommodationTax,
      currency: contractCurrency,
      markupPercent,
    };
  }

  if (agentChannel) {
    const resolvedMarkup = resolveHotelMarkupPercent(hotelNet, contract, { agentChannel: true });
    const hotelMarkup = Math.round(hotelNet * (resolvedMarkup / 100) * 100) / 100;
    const hotelTotal = Math.round((hotelNet + hotelMarkup) * 100) / 100;
    return {
      hotelNet,
      hotelMarkup,
      hotelTotal,
      accommodationTax,
      currency: contractCurrency,
      markupPercent: resolvedMarkup,
    };
  }

  if (publicChannel) {
    const publicSale = calculateBelvederePublicHotelSalePriceEur(hotelNet, contract);
    return {
      hotelNet,
      hotelMarkup: publicSale.hotelMarkupEur,
      hotelTotal: publicSale.salePriceEur,
      listPriceEur: publicSale.listPriceEur,
      listMarkupPercent: publicSale.listMarkupPercent,
      saleDiscountPercent: publicSale.saleDiscountPercent,
      accommodationTax,
      currency: contractCurrency,
      markupPercent: publicSale.effectiveMarkupPercent,
    };
  }

  const publicSale = calculateBelvederePublicHotelSalePriceEur(hotelNet, contract);
  return {
    hotelNet,
    hotelMarkup: publicSale.hotelMarkupEur,
    hotelTotal: publicSale.salePriceEur,
    listPriceEur: publicSale.listPriceEur,
    listMarkupPercent: publicSale.listMarkupPercent,
    saleDiscountPercent: publicSale.saleDiscountPercent,
    accommodationTax,
    currency: contractCurrency,
    markupPercent: publicSale.effectiveMarkupPercent,
  };
}

function allocateSellingAcrossRows(rows, pricing, nights) {
  const netTotal = Number(pricing.hotelNet) || 0;
  if (!rows.length || netTotal <= 0) {
    return rows.map((row) => ({
      ...row,
      markupAmount: 0,
      sellingTotal: 0,
      pricePerNight: 0,
    }));
  }

  let runningMarkup = 0;
  let runningSelling = 0;
  return rows.map((row, index) => {
    const isLast = index === rows.length - 1;
    const share = row.netTotal / netTotal;
    const markupAmount = isLast
      ? Math.round((pricing.hotelMarkup - runningMarkup) * 100) / 100
      : Math.round(pricing.hotelMarkup * share * 100) / 100;
    const sellingTotal = isLast
      ? Math.round((pricing.hotelTotal - runningSelling) * 100) / 100
      : Math.round((row.netTotal + markupAmount) * 100) / 100;
    runningMarkup += markupAmount;
    runningSelling += sellingTotal;
    return {
      ...row,
      markupAmount,
      sellingTotal,
      pricePerNight: nights > 0 ? Math.round((sellingTotal / nights) * 100) / 100 : 0,
    };
  });
}

function buildNetRoomBreakdownRow(room, occupancy, index, arrivalDate, departureDate, contract) {
  const nights = getStayNights(arrivalDate, departureDate);
  const resolved = resolveCompatibleOccupancyForRoom(occupancy, room);
  const pricingOccupancy = resolved?.pricingOccupancy ?? occupancy;
  const incompatibilityReason = resolved
    ? null
    : validateRoomOccupancy(occupancy, room) || "תפוסה לא נתמכת בחדר זה";
  const stayWindowReason = validateRoomStayWindow(room, arrivalDate, departureDate);
  const compatible = Boolean(resolved) && nights > 0 && !stayWindowReason;
  const netTotal = compatible
    ? calculateRoomNet(room, pricingOccupancy, arrivalDate, departureDate, contract)
    : 0;

  return {
    roomIndex: index + 1,
    room,
    roomName: room.name || room.nameHe,
    roomNameHe: room.nameHe,
    // Customer-facing label stays as searched party; pricing may use remapped mix.
    occupancyLabel: formatCampaignRoomOccupancy(occupancy),
    pricingOccupancy,
    isFlexibleOccupancyMatch: Boolean(resolved?.isFlexibleMatch),
    netTotal,
    compatible,
    incompatibilityReason: stayWindowReason || incompatibilityReason || undefined,
    accommodationTax: compatible
      ? Math.round(nights * contract.accommodationTaxPerRoomPerNight * 100) / 100
      : 0,
  };
}

function attachPublicListPriceFields(base, pricing, nights, roomCount = 1) {
  const listPriceEur = pricing?.listPriceEur ?? null;
  const saleDiscountPercent = pricing?.saleDiscountPercent ?? 0;
  const roomQty = Math.max(1, Number(roomCount) || 1);
  const nightCount = Number(nights) || 0;
  const listPricePerNight =
    listPriceEur != null && nightCount > 0
      ? Math.round((listPriceEur / nightCount / roomQty) * 100) / 100
      : null;

  return {
    ...base,
    listPriceEur,
    saleDiscountPercent,
    listPricePerNight,
  };
}

function finalizeMultiRoomPricing(roomBreakdown, roomCount, nights, contract, pricingOptions = {}) {
  const netTotal = sumBreakdownField(roomBreakdown, "netTotal");
  const pricing = buildPriceBreakdown(netTotal, roomCount, nights, undefined, contract, pricingOptions);
  const pricedRows = allocateSellingAcrossRows(roomBreakdown, pricing, nights);

  return {
    netTotal,
    sellingTotal: pricing.hotelTotal,
    listPriceEur: pricing.listPriceEur ?? null,
    saleDiscountPercent: pricing.saleDiscountPercent ?? 0,
    markupAmount: pricing.hotelMarkup,
    markupPercent: pricing.markupPercent,
    accommodationTax: pricing.accommodationTax,
    roomBreakdown: pricedRows,
  };
}

/** Human label for a single room's occupancy in search. */
export function formatCampaignRoomOccupancy(occupancy) {
  const adults = Number(occupancy?.adults) || 0;
  const children = Number(occupancy?.children) || 0;
  const parts = [];
  if (adults > 0) parts.push(adults === 1 ? "מבוגר 1" : `${adults} מבוגרים`);
  if (children > 0) parts.push(children === 1 ? "ילד 1" : `${children} ילדים`);
  return parts.length ? parts.join(" · ") : "—";
}

function compatibleRoomTypesForOccupancy(
  occupancy,
  contract = belvedereContract,
  arrivalDate,
  departureDate,
) {
  return contract.roomTypes.filter(
    (room) =>
      isRoomCompatibleForSearch(occupancy, room) &&
      !validateRoomStayWindow(room, arrivalDate, departureDate),
  );
}

/** Cap multi-room package expansion so 3–5 rooms cannot freeze the UI (cartesian was O(R^N)). */
const BELVEDERE_MULTI_ROOM_BEAM_WIDTH = 240;
const BELVEDERE_MULTI_ROOM_MAX_OFFERS = 80;

function occupancySignature(occupancy) {
  const ages = Array.isArray(occupancy?.childAges)
    ? occupancy.childAges.map((age) => Number(age)).filter((age) => Number.isFinite(age)).sort((a, b) => a - b)
    : [];
  return `${Number(occupancy?.adults) || 0}:${Number(occupancy?.children) || 0}:${ages.join(",")}`;
}

function scoreRoomOptionForOccupancy(room, occupancy, arrivalDate, departureDate, contract) {
  const resolved = resolveCompatibleOccupancyForRoom(occupancy, room);
  if (!resolved) return null;
  const net = calculateRoomNet(
    room,
    resolved.pricingOccupancy ?? occupancy,
    arrivalDate,
    departureDate,
    contract,
  );
  return { room, net: Number(net) || 0 };
}

/**
 * Enumerate multi-room assignments without materializing R^N cartesian products.
 * - Identical occupancies: combinations-with-repetition (order-independent packages).
 * - Mixed occupancies: price-sorted beam search (keeps cheapest partial packages).
 */
function enumerateMultiRoomAssignments(scoredPerRoom, identicalOccupancies) {
  const roomCount = scoredPerRoom.length;
  if (roomCount === 0) return [];

  if (identicalOccupancies) {
    const options = scoredPerRoom[0];
    const combos = [];
    const choose = (start, remaining, accRooms, accNet) => {
      if (remaining === 0) {
        combos.push({ rooms: accRooms.slice(), net: accNet });
        return;
      }
      for (let i = start; i < options.length; i += 1) {
        accRooms.push(options[i].room);
        choose(i, remaining - 1, accRooms, accNet + options[i].net);
        accRooms.pop();
      }
    };
    choose(0, roomCount, [], 0);
    combos.sort((a, b) => a.net - b.net);
    return combos.slice(0, BELVEDERE_MULTI_ROOM_BEAM_WIDTH).map((row) => row.rooms);
  }

  let beam = [{ rooms: [], net: 0 }];
  for (let roomIdx = 0; roomIdx < roomCount; roomIdx += 1) {
    const options = scoredPerRoom[roomIdx];
    const next = [];
    for (const partial of beam) {
      for (const opt of options) {
        next.push({
          rooms: [...partial.rooms, opt.room],
          net: partial.net + opt.net,
        });
      }
    }
    next.sort((a, b) => a.net - b.net);
    beam = next.slice(0, BELVEDERE_MULTI_ROOM_BEAM_WIDTH);
  }
  return beam.map((row) => row.rooms);
}

function sumBreakdownField(rows, field) {
  return Math.round(rows.reduce((sum, row) => sum + (Number(row[field]) || 0), 0) * 100) / 100;
}

function annotateOffer(offer) {
  const breakdown = Array.isArray(offer.roomBreakdown) ? offer.roomBreakdown : [];
  const primaryRoom = breakdown[0]?.room ?? offer.room;
  const isUniformRoomType =
    breakdown.length > 0 && breakdown.every((row) => row.room?.id === primaryRoom?.id);
  const comboId = breakdown.map((row) => row.room?.code ?? "?").join("-") || primaryRoom?.id;
  const componentNames = breakdown
    .map((row) => row.room?.name || row.roomName || row.room?.nameHe || row.roomNameHe)
    .filter((name) => Boolean(name && String(name).trim()));
  const mixedDisplayName =
    componentNames.length === breakdown.length && breakdown.length > 1
      ? componentNames.join(" · ")
      : `חבילת ${breakdown.length || 1} חדרים`;
  const displayName = isUniformRoomType
    ? primaryRoom?.name || primaryRoom?.nameHe
    : mixedDisplayName;

  return {
    ...offer,
    room: primaryRoom,
    id: comboId,
    displayName,
    displayNameHe: displayName,
    isUniformRoomType,
  };
}

function buildComboOffer(
  roomAssignments,
  occupancies,
  arrivalDate,
  departureDate,
  contract,
  pricingOptions = {},
) {
  const nights = getStayNights(arrivalDate, departureDate);
  const roomBreakdown = roomAssignments.map((room, index) =>
    buildNetRoomBreakdownRow(room, occupancies[index], index, arrivalDate, departureDate, contract),
  );

  if (!roomBreakdown.every((row) => row.compatible) || nights === 0) {
    const firstBad = roomBreakdown.find((row) => !row.compatible);
    return annotateOffer({
      room: roomAssignments[0],
      netTotal: 0,
      sellingTotal: 0,
      markupAmount: 0,
      markupPercent: pricingOptions.agentChannel
        ? contract.agentMarkupPercent ?? BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT
        : resolveBelvederePublicHotelEffectiveMarkupPercent(contract),
      pricePerNight: 0,
      nights,
      accommodationTax: 0,
      compatible: false,
      incompatibilityReason: firstBad?.incompatibilityReason,
      roomBreakdown,
      roomCount: roomAssignments.length,
    });
  }

  const priced = finalizeMultiRoomPricing(
    roomBreakdown,
    roomAssignments.length,
    nights,
    contract,
    pricingOptions,
  );

  return annotateOffer(
    attachPublicListPriceFields(
      {
        room: roomAssignments[0],
        netTotal: priced.netTotal,
        sellingTotal: priced.sellingTotal,
        markupAmount: priced.markupAmount,
        markupPercent: priced.markupPercent,
        pricePerNight:
          nights > 0
            ? Math.round((priced.sellingTotal / nights / roomAssignments.length) * 100) / 100
            : 0,
        nights,
        accommodationTax: priced.accommodationTax,
        compatible: true,
        roomBreakdown: priced.roomBreakdown,
        roomCount: roomAssignments.length,
      },
      priced,
      nights,
      roomAssignments.length,
    ),
  );
}

export function quoteRoomOffer(
  room,
  occupancy,
  arrivalDate,
  departureDate,
  roomQuantity = 1,
  contract = belvedereContract,
  pricingOptions = {},
) {
  const nights = getStayNights(arrivalDate, departureDate);
  const resolved = resolveCompatibleOccupancyForRoom(occupancy, room);
  const pricingOccupancy = resolved?.pricingOccupancy ?? occupancy;
  const incompatibilityReason = resolved
    ? null
    : validateRoomOccupancy(occupancy, room) || "תפוסה לא נתמכת בחדר זה";
  const compatible = Boolean(resolved);

  if (!compatible || nights === 0) {
    return {
      room,
      netTotal: 0,
      sellingTotal: 0,
      markupAmount: 0,
      pricePerNight: 0,
      nights,
      accommodationTax: 0,
      compatible,
      incompatibilityReason: incompatibilityReason ?? undefined,
    };
  }

  const singleRoomNet = calculateRoomNet(
    room,
    pricingOccupancy,
    arrivalDate,
    departureDate,
    contract,
  );
  const netTotal = Math.round(singleRoomNet * roomQuantity * 100) / 100;
  const breakdown = buildPriceBreakdown(netTotal, roomQuantity, nights, undefined, contract, pricingOptions);

  return attachPublicListPriceFields(
    {
      room,
      netTotal,
      sellingTotal: breakdown.hotelTotal,
      markupAmount: breakdown.hotelMarkup,
      markupPercent: breakdown.markupPercent,
      pricePerNight:
        nights > 0 ? Math.round((breakdown.hotelTotal / nights / roomQuantity) * 100) / 100 : 0,
      nights,
      accommodationTax: breakdown.accommodationTax,
      compatible: true,
      pricingOccupancy,
      isFlexibleOccupancyMatch: Boolean(resolved?.isFlexibleMatch),
    },
    breakdown,
    nights,
    roomQuantity,
  );
}

export function quoteMultiRoomOffer(
  room,
  occupancies,
  arrivalDate,
  departureDate,
  contract = belvedereContract,
  pricingOptions = {},
) {
  const list = Array.isArray(occupancies) ? occupancies : [occupancies];
  const nights = getStayNights(arrivalDate, departureDate);

  const roomBreakdown = list.map((occ, index) =>
    buildNetRoomBreakdownRow(room, occ, index, arrivalDate, departureDate, contract),
  );

  const allCompatible = roomBreakdown.every((row) => row.compatible);
  if (!allCompatible || nights === 0) {
    const firstBad = roomBreakdown.find((row) => !row.compatible);
    return {
      room,
      netTotal: 0,
      sellingTotal: 0,
      markupAmount: 0,
      markupPercent: pricingOptions.agentChannel
        ? contract.agentMarkupPercent ?? BELVEDERE_AGENT_HOTEL_MARKUP_PERCENT
        : resolveBelvederePublicHotelEffectiveMarkupPercent(contract),
      pricePerNight: 0,
      nights,
      accommodationTax: 0,
      compatible: false,
      incompatibilityReason: firstBad?.incompatibilityReason ?? undefined,
      roomBreakdown,
      roomCount: list.length,
    };
  }

  const priced = finalizeMultiRoomPricing(roomBreakdown, list.length, nights, contract, pricingOptions);

  return annotateOffer(
    attachPublicListPriceFields(
      {
        room,
        netTotal: priced.netTotal,
        sellingTotal: priced.sellingTotal,
        markupAmount: priced.markupAmount,
        markupPercent: priced.markupPercent,
        pricePerNight:
          nights > 0 ? Math.round((priced.sellingTotal / nights / list.length) * 100) / 100 : 0,
        nights,
        accommodationTax: priced.accommodationTax,
        compatible: true,
        roomBreakdown: priced.roomBreakdown,
        roomCount: list.length,
      },
      priced,
      nights,
      list.length,
    ),
  );
}

export function getCompatibleRoomOffers(
  occupancies,
  arrivalDate,
  departureDate,
  contractOrOptions = belvedereContract,
  maybePricingOptions = {},
) {
  const { contract, pricingOptions } = resolveContractAndPricingOptions(
    contractOrOptions,
    maybePricingOptions,
  );
  const dateError = validateStayDates(arrivalDate, departureDate, contract);
  if (dateError) return [];

  const list = Array.isArray(occupancies) ? occupancies : [occupancies];
  if (list.length === 0) return [];

  if (list.length === 1) {
    return compatibleRoomTypesForOccupancy(list[0], contract, arrivalDate, departureDate)
      .map((room) =>
        quoteMultiRoomOffer(room, list, arrivalDate, departureDate, contract, pricingOptions),
      )
      .sort((a, b) => a.sellingTotal - b.sellingTotal);
  }

  const perRoomOptions = list.map((occ) =>
    compatibleRoomTypesForOccupancy(occ, contract, arrivalDate, departureDate),
  );
  if (perRoomOptions.some((options) => options.length === 0)) return [];

  const scoredPerRoom = perRoomOptions.map((options, roomIdx) =>
    options
      .map((room) => scoreRoomOptionForOccupancy(room, list[roomIdx], arrivalDate, departureDate, contract))
      .filter(Boolean)
      .sort((a, b) => a.net - b.net || Number(a.room.code) - Number(b.room.code)),
  );
  if (scoredPerRoom.some((options) => options.length === 0)) return [];

  const seen = new Map();
  const remember = (offer) => {
    if (!offer?.compatible) return;
    const existing = seen.get(offer.id);
    if (!existing || offer.sellingTotal < existing.sellingTotal) {
      seen.set(offer.id, offer);
    }
  };

  // Always surface uniform packages (same room type for every search room).
  for (const room of contract.roomTypes) {
    if (
      !list.every((occ) => isRoomCompatibleForSearch(occ, room)) ||
      validateRoomStayWindow(room, arrivalDate, departureDate)
    ) {
      continue;
    }
    remember(
      quoteMultiRoomOffer(room, list, arrivalDate, departureDate, contract, pricingOptions),
    );
  }

  const identicalOccupancies = list.every((occ) => occupancySignature(occ) === occupancySignature(list[0]));
  for (const assignment of enumerateMultiRoomAssignments(scoredPerRoom, identicalOccupancies)) {
    remember(buildComboOffer(assignment, list, arrivalDate, departureDate, contract, pricingOptions));
  }

  return [...seen.values()]
    .sort((a, b) => a.sellingTotal - b.sellingTotal || String(a.id).localeCompare(String(b.id)))
    .slice(0, BELVEDERE_MULTI_ROOM_MAX_OFFERS);
}

export function explainRoomSearchFailure(
  occupancies,
  arrivalDate,
  departureDate,
  contract = belvedereContract,
) {
  const dateError = validateStayDates(arrivalDate, departureDate, contract);
  if (dateError) return dateError;

  const list = Array.isArray(occupancies) ? occupancies : [occupancies];
  const blockedIndex = list.findIndex(
    (occ) => compatibleRoomTypesForOccupancy(occ, contract).length === 0,
  );
  if (blockedIndex >= 0) {
    return `חדר ${blockedIndex + 1} (${formatCampaignRoomOccupancy(list[blockedIndex])}) אינו מתאים לאף סוג חדר בחוזה. נסו לשנות את הרכב האורחים בחדר הזה.`;
  }

  return "נסו לשנות תאריכים, מספר חדרים או הרכב אורחים.";
}

/** Persistable Hebrew label for hotel_room / offer summary (prefer real room names). */
export function formatBelvedereSelectedRoomLabel(selectedRoom) {
  if (!selectedRoom || typeof selectedRoom !== "object") return null;
  const nested = Array.isArray(selectedRoom.rooms) ? selectedRoom.rooms : [];
  const nestedNames = nested
    .map((row) => String(row?.name || row?.nameHe || "").trim())
    .filter((name) => name && !/^חדר\s+\d+$/i.test(name) && !/^חבילת\s+\d+\s+חדרים$/.test(name));
  if (nestedNames.length > 0) {
    const unique = [...new Set(nestedNames)];
    if (unique.length === 1 && nested.length > 1) {
      return `${unique[0]} · ${nested.length} חדרים`;
    }
    return nestedNames.join(" · ");
  }
  const top = String(
    selectedRoom.name || selectedRoom.nameHe || selectedRoom.displayNameHe || "",
  ).trim();
  return top || null;
}

export function toSelectedRoom(offer) {
  const breakdown = Array.isArray(offer?.roomBreakdown) ? offer.roomBreakdown : [];
  // Always persist per-room rows for multi-room offers so the customer offer page
  // can show real names/images/occupancy even when the package label is generic.
  if (breakdown.length > 1) {
    const nestedRooms = breakdown.map((row) => {
      const nameHe = row.room?.nameHe || row.roomNameHe || null;
      const name = row.room?.name || row.roomName || nameHe;
      return {
        roomCode: row.room?.code ?? null,
        roomId: row.room?.id ?? null,
        name,
        nameHe,
        displayNameHe: name,
        board: row.room?.board || null,
        boardHe: row.room?.boardHe || null,
        chargeType: row.room?.chargeType || null,
        occupancyLabel: row.occupancyLabel || null,
      };
    });
    const primary = breakdown[0]?.room;
    const isUniform = offer.isUniformRoomType !== false && nestedRooms.every(
      (row) => row.roomId && row.roomId === nestedRooms[0].roomId,
    );
    const componentNames = nestedRooms
      .map((row) => row.name)
      .filter((name) => Boolean(name && String(name).trim()));
    const joinedNames =
      componentNames.length === nestedRooms.length ? componentNames.join(" · ") : null;
    const packageName =
      (isUniform ? primary?.name || primary?.nameHe : null) ||
      joinedNames ||
      offer.displayName ||
      offer.displayNameHe ||
      `חבילת ${breakdown.length} חדרים`;
    const comboId =
      (typeof offer.id === "string" && /^\d+(?:-\d+)+$/.test(offer.id) && offer.id) ||
      nestedRooms
        .map((row) => row.roomCode)
        .filter((code) => code != null && Number.isFinite(Number(code)))
        .join("-") ||
      null;
    return {
      name: packageName,
      nameHe: packageName,
      displayNameHe: packageName,
      quantity: breakdown.length,
      ...(comboId ? { comboId, componentCodes: comboId.split("-").map(Number) } : {}),
      ...(isUniform && primary
        ? {
            roomCode: primary.code ?? null,
            roomId: primary.id ?? null,
            board: primary.board || null,
            boardHe: primary.boardHe || null,
            chargeType: primary.chargeType || null,
          }
        : {}),
      rooms: nestedRooms,
    };
  }

  const room = offer?.room ?? breakdown[0]?.room;
  const name = offer.displayName || room?.name || offer.displayNameHe || room?.nameHe;
  return {
    roomCode: room.code,
    roomId: room.id,
    name,
    nameHe: room?.nameHe || name,
    displayNameHe: name,
    board: room.board,
    boardHe: room.boardHe || null,
    chargeType: room.chargeType,
    quantity: breakdown.length || offer?.roomCount || 1,
  };
}

export function formatUsd(amount) {
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);
}

export function formatHotelContractMoney(amount, currency = "EUR") {
  return currency === "USD" ? formatUsd(amount) : formatEur(amount);
}

export function formatEur(amount) {
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatIls(amount) {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function convertEurToIls(eurAmount, eurRate) {
  const eur = Number(eurAmount) || 0;
  const rate = Number(eurRate);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return Math.round(eur * rate);
}

/** Customer/agent display: admin FX rate → ILS primary, contract currency in parentheses. */
export function formatHotelPriceForCustomer(amount, fxRate, currency = "EUR") {
  const ils = convertEurToIls(amount, fxRate);
  const foreign = currency === "USD" ? formatUsd(amount) : formatEur(amount);
  if (ils == null) return foreign;
  return `${formatIls(ils)} (${foreign})`;
}

/** Human label for campaign search occupancy (what the user searched). */
export function formatCampaignSearchOccupancy(rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  const adults = list.reduce((sum, room) => sum + (Number(room.adults) || 0), 0);
  const children = list.reduce((sum, room) => sum + (Number(room.children) || 0), 0);
  const parts = [];
  if (adults > 0) parts.push(adults === 1 ? "מבוגר 1" : `${adults} מבוגרים`);
  if (children > 0) parts.push(children === 1 ? "ילד 1" : `${children} ילדים`);
  if (list.length > 1) parts.push(`${list.length} חדרים`);
  return parts.length ? parts.join(" · ") : "—";
}

/** Regression matrix for occupancy filtering — used by vitest and dev console. */
export const BELVEDERE_OCCUPANCY_REGRESSION_CASES = [
  {
    id: "2a-1c",
    label: "2 adults + 1 child",
    occupancy: { adults: 2, children: 1, childAges: [5] },
    mustIncludeRoomCodes: [7, 9],
  },
  {
    id: "2a-2c",
    label: "2 adults + 2 children",
    occupancy: { adults: 2, children: 2, childAges: [5, 8] },
    mustIncludeRoomCodes: [7, 8, 9],
  },
  {
    id: "2a-3c",
    label: "2 adults + 3 children",
    occupancy: { adults: 2, children: 3, childAges: [5, 5, 5] },
    mustIncludeRoomCodes: [8, 12, 14],
  },
  {
    id: "4a-1c",
    label: "4 adults + 1 child",
    occupancy: { adults: 4, children: 1, childAges: [6] },
    mustIncludeRoomCodes: [8, 12],
  },
  {
    id: "5a",
    label: "5 adults",
    occupancy: { adults: 5, children: 0, childAges: [] },
    mustIncludeRoomCodes: [8, 12],
  },
  {
    id: "1a-2c",
    label: "1 adult + 2 children (flexible → 2A+1C / 3A rooms)",
    occupancy: { adults: 1, children: 2, childAges: [6, 6] },
    // Superior 2–3 pax (price as 2A+1C) + family rooms that accept remapped 2A+1C.
    mustIncludeRoomCodes: [4, 5, 6, 7, 9],
  },
];

export function runBelvedereOccupancyRegressionConsole({
  arrivalDate = "2026-07-10",
  departureDate = "2026-07-15",
  contract = belvedereContract,
} = {}) {
  const rows = BELVEDERE_OCCUPANCY_REGRESSION_CASES.map((testCase) => {
    const offers = getCompatibleRoomOffers(
      [testCase.occupancy],
      arrivalDate,
      departureDate,
      contract,
    );
    const codes = offers.map((offer) => offer.room.code);
    const missing = (testCase.mustIncludeRoomCodes || []).filter((code) => !codes.includes(code));
    return {
      id: testCase.id,
      label: testCase.label,
      compatibleCount: offers.length,
      roomCodes: codes,
      ok: missing.length === 0,
      missingRoomCodes: missing,
    };
  });

  // eslint-disable-next-line no-console
  console.table(rows);
  return rows;
}
